/**
 * Utility functions for converting between frontend schedule formats and backend DisabledDays format
 */

import {
    convert12HourTo24Hour,
    convert24HourTo12Hour,
    TimeRange,
    DaySchedule
} from './weeklyScheduleFormatters';

// Re-export types for convenience
export type { TimeRange, DaySchedule };

export interface SpecialDate {
    id: string;
    date: string;
    isClosedAllDay: boolean;
    timeRanges: TimeRange[];
}

// Re-export the time conversion functions for convenience
export { convert12HourTo24Hour, convert24HourTo12Hour };

/**
 * Format a date string (YYYY-MM-DD) to backend format (MM/DD/YYYY)
 */
export const formatDateForBackend = (dateStr: string): string => {
    if (!dateStr) return '';

    // Parse the date string explicitly to avoid UTC timezone issues
    const [yearStr, monthStr, dayStr] = dateStr.split('-');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);
    const day = parseInt(dayStr, 10);

    return `${month}/${day}/${year}`;
};

/**
 * Parse backend date format (MM/DD/YYYY, M/D/YY, M/D) to frontend format (YYYY-MM-DD)
 */
export const parseDateFromBackend = (backendDate: string): string => {
    if (!backendDate) return '';

    try {
        // Remove any time portion if present
        const datePart = backendDate.split('(')[0].trim();
        const parts = datePart.split('/');

        if (parts.length === 3) {
            let [month, day, year] = parts.map(Number);

            // Validate parsed numbers
            if (isNaN(month) || isNaN(day) || isNaN(year)) {
                return 'none';
            }

            // Handle 2-digit year
            if (year < 100) {
                year += year < 50 ? 2000 : 1900;
            }

            // Validate date ranges
            if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) {
                return 'none';
            }

            return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
        } else if (parts.length === 2) {
            // M/D format - use current year
            const [month, day] = parts.map(Number);

            // Validate parsed numbers
            if (isNaN(month) || isNaN(day)) {
                return 'none';
            }

            // Validate date ranges
            if (month < 1 || month > 12 || day < 1 || day > 31) {
                return 'none';
            }

            const currentYear = new Date().getFullYear();

            return `${currentYear}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
        }

        return 'none';
    } catch (error) {
        return 'none';
    }
};

/**
 * Convert SpecialDate to backend DisabledDays format
 * Examples:
 * - Closed all day: "01/02/2026"
 * - Custom hours: "01/05/2026(13:00-16:30)"
 */
export const specialDateToDisabledDay = (specialDate: SpecialDate): string => {
    const dateStr = formatDateForBackend(specialDate.date);

    if (!dateStr) return '';

    if (specialDate.isClosedAllDay || specialDate.timeRanges.length === 0) {
        return dateStr;
    }

    // Convert time ranges to 24-hour format
    const timeRangeStr = specialDate.timeRanges
        .map(tr => {
            const start = convert12HourTo24Hour(tr.startTime);
            const end = convert12HourTo24Hour(tr.endTime);
            return `${start}-${end}`;
        })
        .join('|');

    return `${dateStr}(${timeRangeStr})`;
};

/**
 * Convert backend DisabledDays format to SpecialDate
 * Examples:
 * - "01/02/2026" -> { date: "2026-01-02", isClosedAllDay: true, timeRanges: [] }
 * - "01/05/2026(13:00-16:30)" -> { date: "2026-01-05", isClosedAllDay: false, timeRanges: [...] }
 * - "1/2" -> { date: "2026-01-02", isClosedAllDay: true, timeRanges: [] }
 */
export const disabledDayToSpecialDate = (disabledDay: string): SpecialDate | null => {
    if (!disabledDay) return null;

    const hasTime = disabledDay.includes('(');
    const datePart = hasTime ? disabledDay.split('(')[0] : disabledDay;
    const date = parseDateFromBackend(datePart);

    if (date === 'none' || !date) return null;

    const specialDate: SpecialDate = {
        id: `date_${Date.now()}_${Math.random()}`,
        date,
        isClosedAllDay: !hasTime,
        timeRanges: []
    };

    if (hasTime) {
        const timePart = disabledDay.split('(')[1].replace(')', '');
        // Support both pipe and comma delimiters
        const timeRanges = timePart.split(/[|,]/).map((range, index) => {
            const [start, end] = range.split('-');
            return {
                id: `time_${Date.now()}_${index}`,
                startTime: convert24HourTo12Hour(start),
                endTime: convert24HourTo12Hour(end)
            };
        });
        specialDate.timeRanges = timeRanges;
    }

    return specialDate;
};

/**
 * Convert array of SpecialDates to backend DisabledDays format (comma-separated)
 */
export const specialDatesToDisabledDays = (specialDates: SpecialDate[]): string => {
    return specialDates
        .filter(sd => sd.date) // Only include dates that have a valid date
        .map(specialDateToDisabledDay)
        .filter(Boolean)
        .join(',');
};

/**
 * Convert backend DisabledDays format (comma-separated) to array of SpecialDates
 */
export const disabledDaysToSpecialDates = (disabledDays: string): SpecialDate[] => {
    if (!disabledDays) return [];

    return disabledDays
        .split(',')
        .map(disabledDay => disabledDayToSpecialDate(disabledDay.trim()))
        .filter(Boolean) as SpecialDate[];
};

/**
 * Get day of week from date string (YYYY-MM-DD)
 */
export const getDayOfWeek = (dateStr: string): string => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    // Parse the date string explicitly to avoid timezone issues
    const [yearStr, monthStr, dayStr] = dateStr.split('-');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10) - 1; // Month is 0-indexed
    const day = parseInt(dayStr, 10);

    // Create date in UTC to get the correct day of week
    const date = new Date(Date.UTC(year, month, day));
    return days[date.getUTCDay()];
};

/**
 * Validate backend DisabledDays format
 */
export const isValidDisabledDayFormat = (disabledDay: string): boolean => {
    if (!disabledDay) return false;

    // Check basic format: date or date(time-time|time-time) or date(time-time,time-time)
    const dateTimePattern = /^\d{1,2}\/\d{1,2}(\/\d{2,4})?(\(\d{2}:\d{2}-\d{2}:\d{2}([|,]\d{2}:\d{2}-\d{2}:\d{2})*\))?$/;
    return dateTimePattern.test(disabledDay);
};

/**
 * Validate and clean DisabledDays string
 */
export const cleanDisabledDays = (disabledDays: string): string => {
    if (!disabledDays) return '';

    const validDays = disabledDays
        .split(',')
        .map(d => d.trim())
        .filter(isValidDisabledDayFormat);

    return validDays.join(',');
};
